import * as yup from "yup";
import {capitalize} from "@mui/material";
import {FormikConfig, FormikHelpers} from "formik";
import {useAddToolToAufgabe, useRemoveToolFromAufgabe, useUpdateAufgabe} from "../../client/hooks";
import {useCallback, useMemo} from "react";
import {Aufgabe, AufgabeDaten} from "../../api";
import {useAufgabenHandling} from "./useAufgabenHandling";
import {useSnackbar} from "notistack";
import {useMutation, useQueryClient} from "react-query";
import {useMandantenHandling} from "../mandanten/useMandantenHandling";
import {useOnboardingHandling} from "../onboarding/useOnboardingHandling";

import type { AufgabeFormValues } from "../../types/aufgabenTypes";
import { buildInitialAufgabeFormValues } from "./utils/buildInitialAufgabeFormValues";

export function useAufgabeUpdateForm(aufgabe: Aufgabe): FormikConfig<AufgabeFormValues> {
    const {translation} = useAufgabenHandling();
    const {getCountryCode, removeNumberPrefix} = useOnboardingHandling();
    const {getMandantIdByObject} = useMandantenHandling();
    const {enqueueSnackbar} = useSnackbar();
    const {mutateAsync} = useAufgabeUpdate(aufgabe.id);
    const queryClient = useQueryClient();
    const addTool = useAddToolToAufgabe();
    const removeTool = useRemoveToolFromAufgabe();

    const schema = yup.object().shape({
        titel: yup.string().required(capitalize(translation('task-attributes-title-required-message'))),
        beschreibung: yup.string().nullable(),
        sprache: yup.string().matches(/^[a-z]{2}-[A-Z]{2}$/, capitalize(translation('task-attributes-language-regex-message'))),
        prompt: yup.string().notRequired(),
        foreignPrompt: yup.string().notRequired(),
        stimmeId: yup.string().transform((v) => (v === '' ? null : v)).nullable(),
        empathie: yup.number().required(capitalize(translation('task-attributes-empathy-required-message'))),
        telefonnummer: yup.string()
            .transform((v) => (v === '' ? null : v))
            .nullable()
            .matches(/^[1-9][0-9]*$/, capitalize(translation('task-test-attributes-phone-validation-failed-message'))),
        mandantId: yup.string().required(capitalize(translation('task-attributes-client-required-message'))),
        schedulerId: yup.string().required(capitalize(translation('task-attributes-scheduler-required-message'))),
        status: yup.string().oneOf(['PAUSIERT', 'LIVE', 'ABGESCHLOSSEN']).required(capitalize(translation('task-attributes-status-required-message'))),
        auftragssystemAuftragId: yup.string().required(),
        speechModel: yup.string().transform((v) => (v === '' ? null : v)).nullable()
    });

    const initialTouched = useMemo(() => ({
        titel: true,
        beschreibung: true,
        sprache: true,
        prompt: true,
        foreignPrompt: true,
        stimmeId: true,
        empathie: true,
        telefonnummer: true,
        mandantId: true,
        schedulerId: true,
        status: true,
        inboundConfigDurationInHours: true,
        useInbound: true,
        updateInboundConfigTimeOnUpdate: true,
        useInboundPrompt: true,
        useSeparateToolsForInbound: true,
        auftragssystemAuftragId: true,
        speechModel: true
    }), []);

    const initialValues = useMemo(
        () => buildInitialAufgabeFormValues({
            aufgabe,
            phoneNormalizer: (n) => removeNumberPrefix(n ?? ''),
        }),
        [aufgabe, removeNumberPrefix]
    );

    const onSubmitUpdateForm = useCallback(async (
        daten: AufgabeFormValues,
        {setSubmitting, setStatus}: FormikHelpers<AufgabeFormValues>
    ): Promise<void> => {
        try {
            await mutateAsync({
                titel: daten.titel,
                beschreibung: daten.beschreibung,
                sprache: daten.sprache,
                stimmeId: daten.stimmeId || undefined,
                telefonnummer: daten.telefonnummer?.trim() ? `${getCountryCode()}${daten.telefonnummer.trim()}` : null,
                empathie: daten.empathie,
                mandantId: getMandantIdByObject(daten.mandantId),
                schedulerId: daten.schedulerId,
                status: daten.status as AufgabeDaten['status'],
                auftragssystem_auftrag_id: daten.auftragssystemAuftragId,
                inbound_config_duration_in_hours: daten.inboundConfigDurationInHours,
                anzahl_anrufversuche_max: daten.anzahlAnrufversucheMax,
                use_inbound: daten.useInbound,
                update_inbound_config_time_on_update: daten.updateInboundConfigTimeOnUpdate,
                use_inbound_prompt: daten.useInboundPrompt,
                use_separate_tools_for_inbound: daten.useSeparateToolsForInbound,
                speechModel: daten.speechModel || undefined
            });
            const originalToolIds = aufgabe.toolIds ?? [];
            const nextToolIds = daten.toolIds ?? [];
            const toolsToAdd = nextToolIds.filter(id => !originalToolIds.includes(id));
            const toolsToRemove = originalToolIds.filter(id => !nextToolIds.includes(id));
            if (toolsToAdd.length > 0 || toolsToRemove.length > 0) {
                try {
                    await Promise.all([
                        ...toolsToAdd.map(id => addTool(aufgabe.id, id)),
                        ...toolsToRemove.map(id => removeTool(aufgabe.id, id)),
                    ]);
                } finally {
                    await queryClient.invalidateQueries(["api", "v1", "aufgaben", aufgabe.id]);
                }
            }
            setStatus(undefined);
            enqueueSnackbar(capitalize(translation('task-update-successful')), {
                variant: "success",
            });
        } catch (error: unknown) {
            enqueueSnackbar(capitalize(translation('task-update-failed')), {
                variant: "error",
            });
            if (error instanceof Error) {
                setStatus(error.message);
            } else {
                setStatus("An unknown error occurred.");
            }
        } finally {
            setSubmitting(false);
        }
    }, [mutateAsync, enqueueSnackbar, translation, getMandantIdByObject, getCountryCode, aufgabe.id, aufgabe.toolIds, addTool, removeTool, queryClient]);

    return useMemo(
        () => ({
            validationSchema: schema,
            validateOnMount: true,
            enableReinitialize: true,
            initialTouched: initialTouched,
            initialValues: initialValues,
            onSubmit: onSubmitUpdateForm
        }),
        [schema, initialValues, onSubmitUpdateForm, initialTouched]
    );
}

function useAufgabeUpdate(aufgabeId: string) {
    const updateAufgabe = useUpdateAufgabe();
    const queryClient = useQueryClient();

    return useMutation(
        (input: AufgabeDaten): Promise<Aufgabe> => {
            return updateAufgabe(aufgabeId, input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["aufgaben", aufgabeId]);
            },
        }
    );
}
